import {ApolloClient, createHttpLink, InMemoryCache, ApolloProvider, from} from '@apollo/client'
import {RetryLink} from '@apollo/client/link/retry'
import {setContext} from '@apollo/client/link/context'
import {onError} from '@apollo/client/link/error'
import DebounceLink from 'apollo-link-debounce'
import {Provider} from 'react-redux'
import store from '../store'
import {useIsAuthenticated, useMsal} from '@azure/msal-react'
import {loginRequest} from '../auth/auth-config'
import {getAccessToken} from '../helper/authHelper'

const useApolloSetup = (App) =>
  function Apolloed() {
    const {instance, accounts, inProgress} = useMsal()
    const isAuthenticated = useIsAuthenticated()
    const abortControllers = {}
    const withAuthHeader = setContext(async ({operationName}, {headers}) => {
      const accessToken = await getAccessToken(instance, accounts, inProgress)
      if (abortControllers[operationName]) {
        abortControllers[operationName].abort()
      }
      abortControllers[operationName] = new globalThis.AbortController()
      if (isAuthenticated) {
        return {
          headers: {
            ...headers,
            authorization: `Bearer ${accessToken}`
          }
        }
      } else {
        instance.loginRedirect(loginRequest)
      }
    })
    const backendURL =
      process.env.NODE_ENV === 'production' ? globalThis.__env.REACT_APP_API_URL : process.env.REACT_APP_API_URL
    const appApiUrl = createHttpLink({
      uri: `${backendURL}/graphql`
    })

    const client = new ApolloClient({
      link: from([
        new DebounceLink(200),
        new RetryLink({
          delay: {
            initial: 300,
            max: Infinity,
            jitter: true
          },
          attempts: {
            max: 5,
            retryIf: (error) => !!error
          }
        }),
        onError(({graphQLErrors, operation, forward}) => {
          if (graphQLErrors) {
            return forward(operation)
          }
        })
      ]).concat(withAuthHeader.concat(appApiUrl)),
      cache: new InMemoryCache({addTypename: false}),
      queryDeduplication: false,
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'ignore'
        },
        query: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all'
        }
      }
    })

    return (
      <ApolloProvider client={client}>
        <Provider store={store}>
          <App />
        </Provider>
      </ApolloProvider>
    )
  }

export default useApolloSetup
